import { FC, useEffect, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, Divider, Popconfirm, Row, Typography } from 'antd';
import { TbPhotoPause, TbRestore } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useWorkspaceWhitelabelTheme } from '@/hooks/useWorkspaceWhitelabelTheme';
import { workspaceService } from '@/services/database';
import { FlexContentRegion } from '@/shared';
import { PlatformForm as Form } from '@/shared/components/forms';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useWorkspaceStore } from '@/stores';
import { defaultDarkTokens } from '@/theme';
import type { Whitelabel, WhitelabelSharedResourcesSettings, WhitelabelWorkspaceThemeSettings } from '@/types';

import WhitelabelSharedResourcesTab from './components/WhitelabelSharedResourcesTab';
import WhitelabelWorkspaceTab from './components/WhitelabelWorkspaceTab';
import {
    clearLogoDevWhitelabelImport,
    createDefaultWhitelabelSettings,
    createLogoDevWhitelabelImport,
    hasWhitelabelCustomizations,
    mergeSharedResources,
    mergeWorkspaceTheme,
    palettePresets,
    pickEditableTokens,
    ThemeMode,
    ThemeTokenKey,
    type LogoDevWhitelabelImport,
} from './configuration';

interface WhitelabelingProps {
    enabled: boolean;
    initialWhitelabel?: Whitelabel | null;
    onSaved?: (whitelabel: Whitelabel) => void;
    setEnabled: (enabled: boolean) => void;
}

const Whitelabeling: FC<WhitelabelingProps> = ({ enabled, initialWhitelabel, onSaved, setEnabled }): JSX.Element => {
    const { selectedWorkspace: workspace } = useWorkspaceStore();
    const { token, resolvedThemeName, themeName } = useAntDesignTheme();
    const { activateWorkspaceTheme, configureWorkspaceTheme, deactivateWorkspaceTheme } = useWorkspaceWhitelabelTheme();
    const [saving, setSaving] = useState(false);
    const [themeMode, setThemeMode] = useState<ThemeMode>('light');
    const [workspaceTheme, setWorkspaceTheme] = useState<WhitelabelWorkspaceThemeSettings>(() =>
        mergeWorkspaceTheme(initialWhitelabel),
    );
    const [savedWorkspaceTheme, setSavedWorkspaceTheme] = useState<WhitelabelWorkspaceThemeSettings>(() =>
        mergeWorkspaceTheme(initialWhitelabel),
    );
    const [sharedResources, setSharedResources] = useState<WhitelabelSharedResourcesSettings>(() =>
        mergeSharedResources(initialWhitelabel),
    );
    const [savedSharedResources, setSavedSharedResources] = useState<WhitelabelSharedResourcesSettings>(() =>
        mergeSharedResources(initialWhitelabel),
    );
    const brandImportBaselineRef = useRef<LogoDevWhitelabelImport | null>(null);

    useEffect(() => {
        const nextWorkspaceTheme = mergeWorkspaceTheme(initialWhitelabel);
        const nextSharedResources = mergeSharedResources(initialWhitelabel);

        setWorkspaceTheme(nextWorkspaceTheme);
        setSavedWorkspaceTheme(nextWorkspaceTheme);
        setSharedResources(nextSharedResources);
        setSavedSharedResources(nextSharedResources);
        brandImportBaselineRef.current = null;
    }, [initialWhitelabel]);

    const updateToken = (key: ThemeTokenKey, value: string | number | undefined) => {
        setWorkspaceTheme((previousTheme) => {
            const nextModeTokens = { ...previousTheme[themeMode], [key]: value };
            const nextTheme = {
                ...previousTheme,
                [themeMode]: nextModeTokens,
                preset: 'custom',
            };

            if (previousTheme.reuseLightThemeForDark && themeMode === 'light') {
                nextTheme.dark = nextModeTokens;
            }

            return nextTheme;
        });
    };

    const updateSharedResourceField = <
        TSection extends keyof WhitelabelSharedResourcesSettings,
        TField extends keyof WhitelabelSharedResourcesSettings[TSection],
    >(
        section: TSection,
        field: TField,
        value: WhitelabelSharedResourcesSettings[TSection][TField],
    ) => {
        setSharedResources((previousResources) => ({
            ...previousResources,
            [section]: {
                ...previousResources[section],
                [field]: value,
            },
        }));
    };

    const applyPalettePreset = (presetKey: string) => {
        const preset = palettePresets.find(({ key }) => key === presetKey);

        if (!preset) {
            return;
        }

        setWorkspaceTheme((previousTheme) => ({
            ...previousTheme,
            dark: previousTheme.reuseLightThemeForDark ? preset.light : preset.dark,
            light: preset.light,
            preset: preset.key,
        }));
    };

    const handleReuseLightThemeChange = (checked: boolean) => {
        setWorkspaceTheme((previousTheme) => ({
            ...previousTheme,
            dark: checked ? previousTheme.light : { ...pickEditableTokens(defaultDarkTokens), ...previousTheme.dark },
            reuseLightThemeForDark: checked,
        }));

        if (checked) {
            setThemeMode('light');
        }
    };

    const handleSave = async () => {
        if (!workspace?.id) {
            return;
        }

        setSaving(true);

        try {
            const payload: Partial<Whitelabel> = {
                brandColor: workspaceTheme.light.colorPrimary || '',
                enabled: true,
                font: workspaceTheme.light.fontFamily || '',
                image: sharedResources.header.logoUrl || '',
                linkedin: sharedResources.footer.linkedin || '',
                name: sharedResources.header.brandName || '',
                sharedResources,
                twitter: sharedResources.footer.twitter || '',
                website: sharedResources.footer.website || '',
                workspaceTheme,
            };
            const saved = await workspaceService.updateWhitelabel(workspace.id, payload);
            const nextSavedWorkspaceTheme = mergeWorkspaceTheme(saved);
            const nextSavedSharedResources = mergeSharedResources(saved);

            setWorkspaceTheme(nextSavedWorkspaceTheme);
            setSavedWorkspaceTheme(nextSavedWorkspaceTheme);
            setSharedResources(nextSavedSharedResources);
            setSavedSharedResources(nextSavedSharedResources);
            brandImportBaselineRef.current = null;
            configureWorkspaceTheme(nextSavedWorkspaceTheme);
            activateWorkspaceTheme();
            onSaved?.(saved);
            setEnabled(saved.enabled);
            toast.success('Whitelabeling updated', {
                description: 'Workspace and shared-resource branding settings were saved.',
            });
        } catch (error) {
            captureException(error);
            toast.error('Whitelabeling could not be saved.');
        } finally {
            setSaving(false);
        }
    };

    const handleDisable = async () => {
        if (!workspace?.id) {
            return;
        }

        setSaving(true);

        try {
            const saved = await workspaceService.updateWhitelabel(workspace.id, { enabled: false });

            onSaved?.(saved);
            setEnabled(false);
            deactivateWorkspaceTheme(String(resolvedThemeName).includes('dark') ? 'dark' : 'light', themeName);
            toast.success('Whitelabeling disabled.');
        } catch (error) {
            captureException(error);
            toast.error('Whitelabeling could not be disabled.');
        } finally {
            setSaving(false);
        }
    };

    const isThemeFieldUpdated = (mode: ThemeMode, key: ThemeTokenKey) =>
        workspaceTheme[mode][key] !== savedWorkspaceTheme[mode][key];
    const isSharedResourceFieldUpdated = <
        TSection extends keyof WhitelabelSharedResourcesSettings,
        TField extends keyof WhitelabelSharedResourcesSettings[TSection],
    >(
        section: TSection,
        field: TField,
    ) => sharedResources[section][field] !== savedSharedResources[section][field];
    const revertThemeField = (mode: ThemeMode, key: ThemeTokenKey) => {
        setWorkspaceTheme((previousTheme) => {
            const restoredValue = savedWorkspaceTheme[mode][key];
            const nextTheme = {
                ...previousTheme,
                [mode]: { ...previousTheme[mode], [key]: restoredValue },
            };

            if (previousTheme.reuseLightThemeForDark && mode === 'light') {
                nextTheme.dark = { ...nextTheme.dark, [key]: restoredValue };
            }

            return nextTheme;
        });
    };
    const revertReuseLightTheme = () => {
        setWorkspaceTheme((previousTheme) => ({
            ...previousTheme,
            dark: savedWorkspaceTheme.dark,
            reuseLightThemeForDark: savedWorkspaceTheme.reuseLightThemeForDark,
        }));

        if (savedWorkspaceTheme.reuseLightThemeForDark) {
            setThemeMode('light');
        }
    };
    const revertSharedResourceField = <
        TSection extends keyof WhitelabelSharedResourcesSettings,
        TField extends keyof WhitelabelSharedResourcesSettings[TSection],
    >(
        section: TSection,
        field: TField,
    ) => updateSharedResourceField(section, field, savedSharedResources[section][field]);
    const hasWhitelabelUpdates = hasWhitelabelCustomizations({ sharedResources, workspaceTheme });
    const clearUpdates = () => {
        const defaults = createDefaultWhitelabelSettings();

        setWorkspaceTheme(defaults.workspaceTheme);
        setSharedResources(defaults.sharedResources);
        brandImportBaselineRef.current = null;

        if (defaults.workspaceTheme.reuseLightThemeForDark) {
            setThemeMode('light');
        }
    };
    const clearImportedBrand = () => {
        if (!brandImportBaselineRef.current && workspaceTheme.preset !== 'brand-import') {
            updateSharedResourceField('footer', 'website', '');
            return;
        }

        const clearedSettings = clearLogoDevWhitelabelImport({
            baseline: brandImportBaselineRef.current,
            sharedResources,
            workspaceTheme,
        });

        setWorkspaceTheme(clearedSettings.workspaceTheme);
        setSharedResources(clearedSettings.sharedResources);
        brandImportBaselineRef.current = null;

        if (clearedSettings.workspaceTheme.reuseLightThemeForDark) {
            setThemeMode('light');
        }
    };

    const workspaceTab = (
        <WhitelabelWorkspaceTab
            disabled={saving}
            sharedResources={sharedResources}
            themeMode={themeMode}
            workspaceTheme={workspaceTheme}
            isReuseLightThemeUpdated={
                workspaceTheme.reuseLightThemeForDark !== savedWorkspaceTheme.reuseLightThemeForDark
            }
            isThemeFieldUpdated={isThemeFieldUpdated}
            isWebsiteUpdated={isSharedResourceFieldUpdated('footer', 'website')}
            onApplyPalettePreset={applyPalettePreset}
            onBrandCleared={clearImportedBrand}
            onBrandImported={(brand, website) => {
                brandImportBaselineRef.current ??= { sharedResources, workspaceTheme };
                const importedSettings = createLogoDevWhitelabelImport({
                    brand,
                    sharedResources,
                    website,
                    workspaceTheme,
                });

                setWorkspaceTheme(importedSettings.workspaceTheme);
                setSharedResources(importedSettings.sharedResources);
                setThemeMode('light');
            }}
            onReuseLightThemeChange={handleReuseLightThemeChange}
            onRevertReuseLightTheme={revertReuseLightTheme}
            onRevertThemeField={revertThemeField}
            onRevertWebsite={() => revertSharedResourceField('footer', 'website')}
            onThemeModeChange={setThemeMode}
            onTokenChange={updateToken}
            onWebsiteChange={(website) => updateSharedResourceField('footer', 'website', website)}
        />
    );

    return (
        <Form
            layout='vertical'
            style={{ display: 'flex', flex: '1 1 0%', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
        >
            <Divider data-whitelabeling-header-divider='true' style={{ flex: 'none', margin: '1rem 0 0' }} />

            <FlexContentRegion data-whitelabeling-scroll-region='true' overflowX='hidden' overflowY='auto'>
                <div style={{ padding: '1rem 0' }}>
                    <section aria-labelledby='workspace-appearance-heading'>
                        <Typography.Title
                            id='workspace-appearance-heading'
                            level={4}
                            style={{ marginBottom: '0.25rem' }}
                        >
                            Workspace
                        </Typography.Title>
                        <Typography.Paragraph style={{ color: token.colorTextTertiary, marginBottom: '1rem' }}>
                            Configure the theme members use throughout this workspace.
                        </Typography.Paragraph>
                        {workspaceTab}
                    </section>

                    <Divider />

                    <section aria-labelledby='shared-resources-appearance-heading'>
                        <Typography.Title
                            id='shared-resources-appearance-heading'
                            level={4}
                            style={{ marginBottom: '0.25rem' }}
                        >
                            Shared Resources
                        </Typography.Title>
                        <Typography.Paragraph style={{ color: token.colorTextTertiary, marginBottom: '1rem' }}>
                            Configure the branded header, content, and footer shown on public resources.
                        </Typography.Paragraph>
                        <WhitelabelSharedResourcesTab
                            isFieldUpdated={isSharedResourceFieldUpdated}
                            onRevertField={revertSharedResourceField}
                            sharedResources={sharedResources}
                            workspaceName={workspace?.name}
                            onFieldChange={updateSharedResourceField}
                        />
                    </section>
                </div>
            </FlexContentRegion>

            <footer aria-label='Whitelabeling actions' data-whitelabeling-actions='true' style={{ flex: 'none' }}>
                <Divider style={{ margin: '0 0 1rem' }} />

                {enabled && hasWhitelabelUpdates ? (
                    <Popconfirm
                        disabled={saving}
                        okButtonProps={{ danger: true }}
                        okText='Clear Updates'
                        placement='top'
                        title='Clear every whitelabeling update and restore the platform defaults?'
                        onConfirm={clearUpdates}
                    >
                        <Button
                            block
                            disabled={saving}
                            icon={<TbRestore aria-hidden='true' size='1rem' />}
                            type='dashed'
                        >
                            Clear Whitelabeling Updates
                        </Button>
                    </Popconfirm>
                ) : null}

                <Row gutter={[16, 0]} style={{ margin: enabled && hasWhitelabelUpdates ? '0.75rem 0 0' : 0 }}>
                    <Col span={12} style={{ paddingLeft: 0 }}>
                        <Button
                            block
                            disabled={!workspace?.id || !enabled}
                            loading={saving}
                            type='primary'
                            onClick={handleSave}
                        >
                            Save Changes
                        </Button>
                    </Col>
                    <Col span={12} style={{ paddingRight: 0 }}>
                        <Popconfirm
                            okButtonProps={{ danger: true }}
                            okText='Disable'
                            overlayStyle={{ width: 320 }}
                            placement='right'
                            title='Are you sure you want to disable whitelabeling for this workspace? Your settings will be retained, but the workspace will use the platform defaults.'
                            onConfirm={handleDisable}
                        >
                            <Button block danger loading={saving} type='dashed'>
                                <TbPhotoPause
                                    size='1rem'
                                    style={{ marginRight: '0.375rem', verticalAlign: '-0.15rem' }}
                                />
                                Disable Whitelabeling
                            </Button>
                        </Popconfirm>
                    </Col>
                </Row>
            </footer>
        </Form>
    );
};

export default Whitelabeling;
